/**
 * Custom Divider Node for Lexical
 * A void/decorator node for horizontal rules with blockId support
 */

import {
  $applyNodeReplacement,
  DOMConversionMap,
  DOMExportOutput,
  DecoratorNode,
  EditorConfig,
  LexicalNode,
  SerializedLexicalNode,
  Spread,
} from 'lexical'

export type SerializedCustomDividerNode = Spread<
  {
    blockId: string
    align?: 'left' | 'right' | 'center'
  },
  SerializedLexicalNode
>

export class CustomDividerNode extends DecoratorNode<JSX.Element> {
  __blockId: string
  __align?: 'left' | 'right' | 'center'

  static getType(): string {
    return 'custom-divider'
  }

  static clone(node: CustomDividerNode): CustomDividerNode {
    return new CustomDividerNode(node.__blockId, node.__align, node.__key)
  }

  constructor(blockId: string, align?: 'left' | 'right' | 'center', key?: string) {
    super(key)
    this.__blockId = blockId
    this.__align = align
  }

  getBlockId(): string {
    return this.__blockId
  }

  setBlockId(blockId: string): this {
    const writable = this.getWritable()
    writable.__blockId = blockId
    return writable
  }

  getAlign(): 'left' | 'right' | 'center' | undefined {
    return this.__align
  }

  setAlign(align: 'left' | 'right' | 'center' | undefined): this {
    const writable = this.getWritable()
    writable.__align = align
    return writable
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const div = document.createElement('div')
    div.setAttribute('data-block-id', this.__blockId)
    div.setAttribute('contenteditable', 'false')
    div.style.margin = '16px 0'
    if (this.__align) {
      div.style.textAlign = this.__align
    }
    
    // Create the actual horizontal rule element
    const hr = document.createElement('hr')
    hr.style.border = 'none'
    hr.style.borderTop = '1px solid var(--editor-divider)'
    hr.style.margin = '0'
    div.appendChild(hr)
    
    return div
  }

  updateDOM(prevNode: CustomDividerNode, dom: HTMLElement): boolean {
    if (prevNode.__blockId !== this.__blockId) {
      dom.setAttribute('data-block-id', this.__blockId)
    }
    if (prevNode.__align !== this.__align) {
      if (this.__align) {
        dom.style.textAlign = this.__align
      } else {
        dom.style.textAlign = ''
      }
    }
    return false
  }

  static importDOM(): DOMConversionMap | null {
    return {
      hr: () => ({
        conversion: (element: HTMLElement) => {
          const blockId = element.getAttribute('data-block-id') || ''
          return {
            node: $createCustomDividerNode(blockId),
          }
        },
        priority: 1,
      }),
    }
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('hr')
    element.setAttribute('data-block-id', this.__blockId)
    return { element }
  }

  static importJSON(serializedNode: SerializedCustomDividerNode): CustomDividerNode {
    return $createCustomDividerNode(serializedNode.blockId, serializedNode.align)
  }

  exportJSON(): SerializedCustomDividerNode {
    return {
      type: 'custom-divider',
      version: 1,
      blockId: this.__blockId,
      align: this.__align,
    }
  }

  isInline(): boolean {
    return false
  }

  decorate(): JSX.Element {
    // Return a simple horizontal rule element
    // The actual rendering is handled in LexicalEditor component
    return null as unknown as JSX.Element
  }
}

export function $createCustomDividerNode(
  blockId: string,
  align?: 'left' | 'right' | 'center',
): CustomDividerNode {
  return $applyNodeReplacement(new CustomDividerNode(blockId, align))
}

export function $isCustomDividerNode(node: LexicalNode | null | undefined): node is CustomDividerNode {
  return node instanceof CustomDividerNode
}

